import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslate } from "../useTranslate";
import { useSelector } from "react-redux";
import Icon from "./icon";
import Avatar from "./avatar";
import BrandLogo from "./brand-logo";
import {useWorkspaceRole} from "../use-workspace-role";
import {canAccessWorkspacePage, canManageWorkspaceSettings} from "../workspace-permissions";

function Sidebar({ onChange, expanded, extraPages = [] }) {
    const { config, pages, homePageIndex } = useSelector((state) => state.app);
    const {user} = useSelector(state => state.auth);
    const {workspaceMembership, workspaceRole} = useWorkspaceRole();

    const navigate = useNavigate();
    const location = useLocation();
    const translate = useTranslate();

    const [currentPageIndex, setCurrentPageIndex] = useState(homePageIndex);
    const [openGroups, setOpenGroups] = useState({});
    function toggleGroup(name) {
        setOpenGroups(prev => ({ ...prev, [name]: !prev[name] }));
    }

    function groupPages(pagesList) {
        const groups = {};
        const roots = [];

        pagesList.forEach((p) => {
            if (p.parent) {
                if (!groups[p.parent]) groups[p.parent] = [];
                groups[p.parent].push(p);
            } else {
                roots.push(p);
            }
        });

        return { groups, roots };
    }

    // ✅ pages و extraPages ادغام می‌شن (extraPages هم دقیقاً مثل pages رفتار می‌کنه)
    const mergedPages = useMemo(() => {
        const base = Array.isArray(pages) ? pages : [];
        const extra = Array.isArray(extraPages) ? extraPages : [];

        // جلوگیری از تکراری شدن: اگر name تکراری بود، extraPages اولویت می‌گیره
        const byName = new Map();
        base.forEach(p => p?.name && byName.set(p.name, p));
        extra.forEach(p => p?.name && byName.set(p.name, p));

        const allPages = Array.from(byName.values());
        if (!workspaceMembership) return allPages;
        return workspaceRole
            ? allPages.filter((page) => canAccessWorkspacePage(workspaceRole, page))
            : [];
    }, [pages, extraPages, workspaceMembership, workspaceRole]);

    const { groups, roots } = useMemo(() => groupPages(mergedPages), [mergedPages]);

    // 1) تعیین currentPageIndex بر اساس مسیر
    useEffect(() => {
        // console.log(`/${location.pathname.split("/")[1]}`)
        const idx =
            location.pathname === "/"
                ? homePageIndex
                : mergedPages.findIndex(e => location.pathname === e["nav-url"]);

        setCurrentPageIndex(idx === -1 ? homePageIndex : idx);
    }, [location.pathname, mergedPages, homePageIndex]);

    // 2) عنوان صفحه
    useEffect(() => {
        const p = mergedPages[currentPageIndex];
        if (p) {
            document.title = `${translate(config["app-name"])} - ${translate(p.name)}`;
        } else {
            document.title = `${translate(config["app-name"])}`;
        }
    }, [currentPageIndex, mergedPages, config, translate]);

    // 3) مقداردهی اولیه openGroups (پیش‌فرض + باز شدن گروهِ صفحه‌ی فعلی)
    useEffect(() => {
        const initial = {};

        mergedPages.forEach(p => {
            if (p["is-group"]) initial[p.name] = !!p["default-open"];
        });

        const current = mergedPages.find(p => p["nav-url"] === location.pathname);
        if (current?.parent) initial[current.parent] = true;

        setOpenGroups(prev => ({ ...initial, ...prev }));
    }, [mergedPages, location.pathname]);

    return (
        <div className={`${expanded ? "w-16rem" : "w-6rem"} transition-ease-in-out transition-duration-400 h-full border-right-1 border-left-1 border-700 flex flex-column overflow-y-auto scrollbar-hide overflow-x-hidden surface-900`}>
            {/* Header */}
            <div className={`flex p-2 align-items-center ${!expanded && "justify-content-center"} border-bottom-1 border-700 h-4rem`}>
                <BrandLogo variant="mark" className="w-3rem h-3rem cursor-pointer" onClick={() => navigate("/")}/>

                {expanded && (
                    <div className="flex flex-column pl-2 pr-2">
                        <span className="text-2xs w-10rem text-gray-400">
                            {translate("app-description")}
                        </span>
                    </div>
                )}
            </div>

            {/* Items */}
            <div className={`w-full h-full ${expanded?"p-3":"p-2 pr-0"} pl-0 gap-2 flex flex-column flex-1 overflow-hidden`}>
                <div className="flex flex-column w-full overflow-auto h-full scrollbar-hide text-500">
                    {roots.map((e, index) => {
                        if (e["is-group"]) {
                            const isOpen = !!openGroups[e.name];

                            return (
                                <div key={e.name} className="mt-1 mb-1 surface-900 border-round ">
                                    <div
                                        className={`flex align-items-center p-2 cursor-pointer ${expanded ? "pl-3 pr-3" : "justify-content-center"}`}
                                        onClick={() => toggleGroup(e.name)}
                                    >
                                        {expanded && (
                                            <span className="text-sm font-bold flex-1">
                                                {translate(e.name)}
                                            </span>
                                        )}
                                        <i
                                            className={`text-xs pi pi-chevron-${isOpen ? "down" : "right"} transition-all transition-duration-300`}
                                            style={{ transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)" }}
                                        />
                                    </div>

                                    <div
                                        className="overflow-hidden transition-all transition-duration-300"
                                        style={{ maxHeight: isOpen ? "500px" : "0px", opacity: isOpen ? 1 : 0 }}
                                    >
                                        <div className="pl-2 pr-1 pb-2 pt-1">
                                            {groups[e.name]?.map((p) => (
                                                    <SidebarItem
                                                        key={p.name}
                                                        expanded={expanded}
                                                        icon={p.icon}
                                                        disabled={p.disabled}
                                                        link={p.link}
                                                        label={p.name}
                                                        isChild={true}
                                                        selected={`/${location.pathname.split("/")[1]}` === p["nav-url"]}
                                                        onClick={() => {
                                                            navigate(p["nav-url"]);
                                                            const idx = mergedPages.indexOf(p);
                                                            setCurrentPageIndex(idx);
                                                            onChange && onChange({ index: idx });
                                                        }}
                                                    />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            );
                        }

                        return (
                            e["show-in-sidebar"] && (
                                    <SidebarItem
                                        key={e.name}
                                        expanded={expanded}
                                        icon={e.icon}
                                        disabled={e.disabled}
                                        label={e.name}
                                        selected={`/${location.pathname.split("/")[1]}` === e["nav-url"]}
                                        onClick={() => {
                                            navigate(e["nav-url"]);
                                            setCurrentPageIndex(index);
                                            onChange && onChange({ index });
                                        }}
                                    />

                            )
                        );
                    })}
                </div>
            </div>
            <div className={`flex flex-column mt-auto pb-2 text-500 ${expanded?"p-3":"p-2 pr-0"} pt-0 pl-0 border-top-1 border-700`}>

                {/* Settings */}
                {canManageWorkspaceSettings(workspaceRole) && (
                    <SidebarItem
                        expanded={expanded}
                        icon={"mi mi:settings"}
                        disabled={false}
                        label={"settings"}
                        selected={`/${location.pathname.split("/")[1]}`=== "/settings"}
                        onClick={() => navigate("/settings")}
                    />
                )}

                {/* User Account (Avatar icon) */}
                {workspaceRole?.role !== "guest" && (
                    <SidebarItem
                        expanded={expanded}
                        avatar={
                            <Avatar memberData={user} bgColorRange="500" borderColorRange={0} className="text-900 text-2xs font-bold"/>
                        }
                        disabled={true}
                        label={user.fullname}
                        selected={`/${location.pathname.split("/")[1]}` === "/user-account"}
                        onClick={() => navigate("/user-account")}
                    />
                )}
            </div>
        </div>
    );
}
function SidebarItem({
                         label,
                         selected,
                         onClick,
                         disabled,
                         icon,
                         avatar,
                         expanded,
                         link,
                         isChild,
                     }) {
    const translate = useTranslate();

    const ItemContent = (
        <div className={`
        ${expanded?"p-3":"p-2"}
        ${isChild ? (expanded ? "pl-5 pr-3" : "pl-2") : "pl-3 pr-3"}
        border-round-lg
        
        w-full
        cursor-pointer
        text-sm
        flex
        ${expanded?"flex-row":"flex-column text-2xs"}
        align-items-center
        gap-2
        white-space-nowrap
        ${!expanded && "justify-content-center"}
        ${selected ? "text-50" : "hover:text-400"}
        ${disabled && "p-disabled"}
      `}
            style={{
                fontSize: isChild ? "0.82rem" : "0.9rem",
                opacity: isChild ? 0.85 : 1,
                position: "relative"
            }}
            onClick={!link ? onClick : undefined}>
            {expanded ? (
                <>
                    {icon && <Icon icon={icon} />}
                    {avatar}
                </>
            ) : (
                <span className="relative inline-flex align-items-center justify-content-center">
                    {icon && <Icon icon={icon} />}
                    {avatar}
                </span>
            )}
            {<span>{translate(label)}</span>}
            {selected && (
                <span
                    style={{
                        position: "absolute",
                        top: "50%",
                        transform: "translateY(-50%)",
                        width: "6px",
                        height: expanded?"70%":"50%",
                        background: "#ff6a00",
                        borderTopRightRadius: "8px",
                        borderBottomRightRadius: "8px",
                        left: "-1px",
                        pointerEvents: "none"
                    }}
                />
            )}
        </div>
    );

    if (link) {
        return (
            <a
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                className="no-underline text-color"
            >
                {ItemContent}
            </a>
        );
    }

    return ItemContent;
}

export default Sidebar;
